import React, { useCallback, useEffect, useState } from 'react';
import { Activity, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { observabilityService } from '../services/api';
import { userFacingError } from '../i18n/userFacingError';

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.items ?? payload?.data ?? payload?.events ?? [];
}

export default function SuperAdminObservability() {
  const [filters, setFilters] = useState({ tenant_org_id: '', event_type: '', job_id: '' });
  const [events, setEvents] = useState([]);
  const [health, setHealth] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.tenant_org_id.trim()) params.tenant_org_id = filters.tenant_org_id.trim();
      if (filters.event_type.trim()) params.event_type = filters.event_type.trim();
      if (filters.job_id.trim()) params.job_id = filters.job_id.trim();
      const [{ data: ev }, { data: h }] = await Promise.all([
        observabilityService.listEvents({ limit: 200, ...params }),
        observabilityService.health().catch(() => ({ data: { status: 'degraded' } })),
      ]);
      setEvents(unwrap(ev));
      setHealth(h);
      if (filters.job_id.trim()) {
        const { data } = await observabilityService.getTimeline(filters.job_id.trim());
        setTimeline(unwrap(data));
      } else {
        setTimeline([]);
      }
    } catch (e) {
      toast.error(userFacingError(e, 'loadError'));
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="form-group mb-0">
          <label className="form-label">Tenant</label>
          <input
            className="form-input"
            value={filters.tenant_org_id}
            onChange={(e) => setFilters((f) => ({ ...f, tenant_org_id: e.target.value }))}
            placeholder="UUID da organização"
          />
        </div>
        <div className="form-group mb-0">
          <label className="form-label">Tipo de evento</label>
          <input
            className="form-input"
            value={filters.event_type}
            onChange={(e) => setFilters((f) => ({ ...f, event_type: e.target.value }))}
          />
        </div>
        <div className="form-group mb-0">
          <label className="form-label">Job</label>
          <input
            className="form-input"
            value={filters.job_id}
            onChange={(e) => setFilters((f) => ({ ...f, job_id: e.target.value }))}
          />
        </div>
        <button type="button" className="btn btn-outline" onClick={load}>
          <RefreshCw className="mr-1 h-4 w-4" />
          Atualizar
        </button>
        <p className="text-sm text-gray-500">
          <Activity className="mr-1 inline h-4 w-4" />
          Health: {health?.status || '—'}
        </p>
      </div>
      {loading ? <p className="text-sm text-gray-500">Carregando eventos…</p> : null}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Quando</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Tipo</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Job</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Tenant</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {events.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                  Nenhum evento no recorte.
                </td>
              </tr>
            ) : (
              events.map((row, idx) => (
                <tr key={row.id || row.correlation_id || idx}>
                  <td className="px-4 py-2">{String(row.ts || row.created_at || row.timestamp || '—').slice(0, 19)}</td>
                  <td className="px-4 py-2">{row.event_type || row.type || '—'}</td>
                  <td className="px-4 py-2 font-mono text-xs">{row.job_id || '—'}</td>
                  <td className="px-4 py-2 font-mono text-xs">{row.tenant_org_id || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {timeline.length > 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold">Timeline do job</h3>
          <ol className="mt-2 space-y-1 text-sm text-gray-700">
            {timeline.map((row, idx) => (
              <li key={row.id || idx}>
                {String(row.ts || row.created_at || '').slice(0, 19)} · {row.event_type || row.type || 'evento'}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}
